import { ActionButton, DirectionalHint, IconButton, ProgressIndicator, removeOnThemeChangeCallback, Stack, TooltipHost } from '@fluentui/react';
import { INavButtonProps, INavLink, INavLinkGroup, INavStyles, Nav } from '@fluentui/react/lib/Nav';
import * as React from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isKeplerAccreditor, shouldGetCourseDataFromCanvasAPIs } from '../../lib/helper';
import { assignmentSupplementalFolders } from '../../pages/course';
import { useAssignmentContext } from '../../providers/assignmentsprovider';
import { useAuthenticationContext } from '../../providers/authenticationprovider';
import { HelperService } from '../../services/helperService';
import { ICourse, ISemester, Semester } from '../../types/ICourse';
import "../application/app.css";

const KEY_NAV_EXPANDED = "kepler-nav-expanded";


const assignmentSupplementalFoldersIconMapping = {
    "Assignment Descriptions": "Info",
    "Syllabus": "ReadingMode",
    "Supplemental Documents": "Documentation",
    "Featured Works": "PhotoCollection"
}



export const LeftNav: React.FunctionComponent<{ course?: ICourse }> = ({ course }) => {

    const assignmentsContext = useAssignmentContext();
    const authContext = useAuthenticationContext();
    const params = useParams();
    const navigate = useNavigate();
    const [navExpanded, setNavExpanded] = useState(localStorage.getItem(KEY_NAV_EXPANDED) === "false" ? false : true);
    const [canvasHostUrl, setCanvasHostUrl] = useState<string | undefined>();
    const [shouldLeftNavGetCourseDataFromCanvasAPIs, setShouldLeftNavGetCourseDataFromCanvasAPIs] = useState<boolean>(false);

    const adminNavLinks = [{
        name: 'Course Prefixes',
        url: "#",
        icon: 'PublishCourse',
        onClick: () => {
            navigate(`/Settings/coursePrefixes`)
        }
    },
    {
        name: 'Accreditors',
        url: "#",
        icon: 'People',
        onClick: () => {
            navigate(`/Settings/accreditors`)
        }
    },
    {
        name: 'Rule Definitions',
        url: "#",
        icon: 'GroupedList',
        onClick: () => {
            navigate(`/Settings/ruleDefinitions`)
        }
    },
    {
        name: 'Criteria Definitions',
        icon: 'NumberedList',
        url: "#",
        onClick: () => {
            navigate(`/Settings/criteriaDefinitions`)
        }
    }]

    React.useEffect(() => {
        try {
            if (params.year && params.semester) {
                HelperService.getKeplerEnvironmentVariable("REACT_APP_DEFAULT_CANVAS_HOST").then(canvasUrl => {
                    setCanvasHostUrl(canvasUrl);
                });
                const semester: ISemester = {
                    Semester: params.semester as Semester,
                    Year: Number(params.year)
                }
                authContext.Result && shouldGetCourseDataFromCanvasAPIs(semester, authContext.Result).then(res => {
                    setShouldLeftNavGetCourseDataFromCanvasAPIs(res || false);
                })
            }
        }
        catch (err) {
            console.log("Unable to get canvas host URL");
        }
    }, [authContext, params.semester, params.year])

    const navStyles: Partial<INavStyles> = {
        root: {
            width: navExpanded ? 308 : 40,
            //height: 350,
            //backgroundColor: "#F5F5F5",
            boxSizing: 'border-box',
            overflowY: 'auto',
            paddingLeft: 4
        },
        groupContent: {
            marginBottom: 0,
        },
        chevronButton: {
            right: 8,
            left: "unset"
        },
    };

    const linkAs = (props: INavButtonProps) => {
        if ((props.link?.expandAriaLabel === "Settings" || props.link?.expandAriaLabel === "Assignments") && !navExpanded) {
            let subNav = undefined;
            if (course && (assignmentsContext.Result || assignmentsContext.Pending)) {
                subNav = <Stack>
                    <h3 style={{ marginLeft: 30 }}>Assignments</h3>
                    {assignmentsContext.Pending && <ProgressIndicator label="Loading assignments..." />}
                    {assignmentsContext.Result &&
                        <Nav
                            selectedKey={assignmentsContext.SelectedAssignment?.Name || "kepler-nav-none"}
                            ariaLabel="Assignments Navigation"
                            groups={[{
                                links: assignmentsContext.Result.filter(a => !assignmentSupplementalFolders.includes(a.Name)).map(a => {
                                    let navLink: INavLink = {
                                        name: a.Name,
                                        url: '#',
                                        key: a.Name,
                                        onClick: () => {
                                            assignmentsContext.SetSelectedAssignment(a);
                                        }
                                    };
                                    return navLink;
                                })
                            }]} />}</Stack>
            }
            else if(!course)
            {
                subNav = <Stack>
                <h3 style={{ marginLeft: 10 }}>Settings</h3>            
                <Nav
                    selectedKey={assignmentsContext.SelectedAssignment?.Name || "kepler-nav-none"}
                    ariaLabel="Settings"
                    groups={[{
                        links: adminNavLinks
                    }]} />
                    </Stack>
            }
            else {
                subNav = <ActionButton  {...props} title='' />
            }

            return (
                <TooltipHost closeDelay={800} directionalHint={DirectionalHint.leftTopEdge} content={subNav}>
                    <ActionButton  {...props} />
                </TooltipHost>
            )
        }
        else {
            return (
                <TooltipHost directionalHint={DirectionalHint.leftCenter} content={!navExpanded ? (props.title && props.title.length > 0 ? props.title : props.link?.ariaLabel) : undefined}>
                    <ActionButton  {...props} />
                </TooltipHost>
            )
        }
    }

    const _getTopLevelLinks = (): INavLink[] => {
        if (course) {
            let links: INavLink[] = [
                {
                    name: "Kepler Course Index",
                    url: "#",
                    icon: "Library",
                    onClick: () => {
                        assignmentsContext.SetSelectedAssignment(undefined);
                        navigate("/")
                    }
                },
                {
                    name: navExpanded ? 'Course Home' : '',
                    url: '#',
                    icon: 'Home',
                    ariaLabel: "Go to course home",
                    onClick: () => {
                        assignmentsContext.SetSelectedAssignment(undefined);
                        navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}`)
                    },
                    expandAriaLabel: 'Course Home',
                    collapseAriaLabel: 'Course Home'
                },
                {
                    name: navExpanded ? 'Assignments' : '',
                    icon: 'NumberedList',
                    expandAriaLabel: 'Assignments',
                    collapseAriaLabel: 'Assignments',
                    url: "#",
                    links: (navExpanded && assignmentsContext.Result) ? assignmentsContext.Result.filter(a => !assignmentSupplementalFolders.includes(a.Name)).map(a => {
                        let navLink: INavLink = {
                            name: a.Name,
                            url: '#',
                            key: a.Name,
                            title: a.Name,
                            onClick: () => {
                                navigate(`/courses/${params.year}/${params.semester}/${encodeURIComponent(course.SIS || course.Course)}/assignments/${encodeURIComponent(a.Name)}`)
                                assignmentsContext.SetSelectedAssignment(a);
                            }
                        };
                        return navLink;
                    }) : (assignmentsContext.Pending ? [{ name: "Loading...", "url": "#" }] : []),
                    isExpanded: true,
                },

            ];

            if (authContext.Result && shouldLeftNavGetCourseDataFromCanvasAPIs && !isKeplerAccreditor(authContext.Result) && course.CanvasId) {
                links.splice(1, 0, {
                    name: navExpanded ? 'Canvas' : '',
                    url: `https://${canvasHostUrl}/courses/${course.CanvasId}`,
                    target: "_blank",
                    ariaLabel: "Go to course in Canvas",
                    icon: 'PublishCourse',
                    expandAriaLabel: 'Link to course in Canvas',
                    collapseAriaLabel: 'Link to course in Canvas'
                })
            }

            if (authContext.Result && shouldLeftNavGetCourseDataFromCanvasAPIs && course.CanvasId) {
                links.push({
                    name: navExpanded ? 'Canvas Syllabus' : '',
                    url: '#',
                    key: 'canvasSyllabus',
                    ariaLabel: 'Syllabus',
                    icon: 'BookAnswers',
                    onClick: () => {
                        assignmentsContext.SetSelectedAssignment(undefined)
                        navigate(`/courses/${params.year}/${params.semester}/${encodeURIComponent(course.SIS || course.Course)}/canvassyllabus`)
                    }
                });
            }
            const supplementalFolders = assignmentsContext.Result?.filter(a => assignmentSupplementalFolders.includes(a.Name));
            supplementalFolders?.forEach(supplementalFolder => {
                links.push({
                    name: navExpanded ? supplementalFolder.Name : '',
                    url: '#',
                    key: supplementalFolder.Name,
                    ariaLabel: supplementalFolder.Name,
                    icon: (assignmentSupplementalFoldersIconMapping as any)[supplementalFolder.Name] || 'Info',
                    onClick: () => {
                        assignmentsContext.SetSelectedAssignment(supplementalFolder);
                        navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}/${supplementalFolder.Name}`)
                    },
                });
            });

            if (authContext.Result?.KeplerAuthStatus?.isKeplerAdministrator)
            {
                links.push({
                    name: navExpanded ? "Accreditor Course Settings" : "",
                    icon: "Settings",
                    url: "#",
                    onClick: () => {
                        assignmentsContext.SetSelectedAssignment(undefined);
                        navigate(`/courses/${course.Year}/${course.Semester}/${encodeURIComponent(course.SIS || course.Course)}/accreditorCourseSettings`, { state: course })
                    },
                    expandAriaLabel: 'Accreditor Course Settings',
                    collapseAriaLabel: 'Accreditor Course Settings'
                });
            }

            return links;
        }
        else {
            let retVal: INavLink[] = [{
                name: navExpanded ? 'Kepler Course Index' : '',
                url: '#',
                icon: 'Home',
                onClick: () => {
                    navigate("/")
                },
                expandAriaLabel: 'Home',
                collapseAriaLabel: 'Home'
            }];

            if (authContext.Result?.KeplerAuthStatus?.isKeplerAdministrator) {
                retVal.push({
                    name: "Settings",
                    url: '#',
                    icon: 'Settings',
                    onClick: () => {
                        navigate("/settings")
                    },
                    expandAriaLabel: 'Settings',
                    collapseAriaLabel: 'Settings',
                    isExpanded: true,
                    links: navExpanded ? adminNavLinks : []
                });
            }
            retVal.push({
                name: "Help",
                url: 'https://ist.njit.edu/servicedesk',
                icon: 'Help',                
                expandAriaLabel: 'Help',
                collapseAriaLabel: 'Help',
                target: "_blank"
            })
            retVal.push({
                name: "Beta user feedback",
                url: 'https://njit.service-now.com/sp?id=keplerfeedback',
                icon: 'Feedback',                
                expandAriaLabel: 'Beta user feedback',
                collapseAriaLabel: 'Beta user feedback',
                target: "_blank"
            })
            return retVal;
        }        
    };

    const _getNavLinkGroups = (): INavLinkGroup[] => {
        let retVal: INavLinkGroup[] = [
            {
                links: _getTopLevelLinks()
            },
        ];
        return retVal;
    }

    return (
        <div className='nav-wrapper'>
            <Stack>
                <IconButton title={navExpanded ? "Collapse Navigation" : "Expand Navigation"} iconProps={{ iconName: "GlobalNavButton" }} style={{ paddingLeft: 10 }} onClick={() => {
                    localStorage.setItem(KEY_NAV_EXPANDED, (!navExpanded).toString());
                    setNavExpanded(!navExpanded);
                }} />
                <div style={{ height: 1, width: navExpanded ? 300 : 24, marginLeft: navExpanded ? 4 : 8, backgroundColor: "#6F6F6F" }} />
                <Nav
                    linkAs={linkAs}
                    selectedKey={assignmentsContext.SelectedAssignment?.Name || "kepler-nav-none"}
                    ariaLabel="Assignments Navigation"
                    styles={navStyles}
                    groups={_getNavLinkGroups()}
                />
            </Stack>
        </div>
    );
};
